import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Select from './ui/Select';

function AdminPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, isActive, logout, getToken, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '',
    isActive: false,
    admin: false,
    subscriptionPlatform: '',
    subscriptionRenewsAt: ''
  });
  const [successMessage, setSuccessMessage] = useState(null);
  const [showGuests, setShowGuests] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorLogsLoading, setErrorLogsLoading] = useState(false);
  const [errorSourceFilter, setErrorSourceFilter] = useState('');
  const [errorBreakingFilter, setErrorBreakingFilter] = useState('');
  const [expandedErrorId, setExpandedErrorId] = useState(null);

  useEffect(() => {
    if (!authLoading && isAuthenticated && isAdmin && isActive) {
      fetchUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, isAdmin, isActive]);

  useEffect(() => {
    if (!authLoading && isAuthenticated && isAdmin && isActive && activeTab === 'errors') {
      fetchErrorLogs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, isAdmin, isActive, activeTab, errorSourceFilter, errorBreakingFilter]);

  const fetchErrorLogs = async () => {
    try {
      setErrorLogsLoading(true);
      const params = {};
      if (errorSourceFilter) params.source = errorSourceFilter;
      if (errorBreakingFilter) params.breaking = errorBreakingFilter;
      const response = await axios.get(`${getApiBaseUrl()}/api/errors`, {
        params,
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setErrorLogs(response.data || []);
    } catch (err) {
      console.error('Failed to fetch error logs:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setErrorLogsLoading(false);
    }
  };

  const fetchUsers = async (guestsFlag = showGuests) => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/admin/users`, {
        params: { showGuests: guestsFlag },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const updateData = {
        email: userFormData.email,
        isActive: userFormData.isActive,
        admin: userFormData.admin
      };
      if (userFormData.subscriptionPlatform !== undefined && userFormData.subscriptionPlatform !== '') {
        updateData.subscriptionPlatform = userFormData.subscriptionPlatform === 'null' ? null : userFormData.subscriptionPlatform;
      }
      if (userFormData.subscriptionRenewsAt !== undefined && userFormData.subscriptionRenewsAt !== '') {
        updateData.subscriptionRenewsAt = userFormData.subscriptionRenewsAt === 'null' ? null : userFormData.subscriptionRenewsAt;
      }
      await axios.put(`${getApiBaseUrl()}/api/admin/users/${editingUser._id}`, 
        updateData,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSuccessMessage('User updated successfully!');
      setEditingUser(null);
      fetchUsers();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to update user:', err);
      alert(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      return;
    }
    try {
      setLoading(true);
      await axios.delete(`${getApiBaseUrl()}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSuccessMessage('User deleted successfully!');
      fetchUsers();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const startEditingUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setUserFormData({
      email: userToEdit.email,
      isActive: userToEdit.isActive,
      admin: userToEdit.admin,
        subscriptionPlatform: userToEdit.subscriptionPlatform || '',
      subscriptionRenewsAt: userToEdit.subscriptionRenewsAt ? new Date(userToEdit.subscriptionRenewsAt).toISOString().slice(0, 16) : ''
    });
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setUserFormData({
      email: '',
      isActive: false,
      admin: false,
        subscriptionPlatform: '',
      subscriptionRenewsAt: ''
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin || !isActive) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">You need admin privileges to access this page.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-muted-foreground mt-1">Manage Lingo users and settings</p>
            </div>
          </div>

          {successMessage && (
            <div className="mb-6 bg-success/20 border border-success text-success px-4 py-3 rounded-lg">
              {successMessage}
            </div>
          )}

          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                activeTab === 'users'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('errors')}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                activeTab === 'errors'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              Error Logs
            </button>
          </div>

          {activeTab === 'users' && (
          <div className="bg-card rounded-lg shadow-lg border border-border">
            <div className="bg-primary/10 px-6 py-4 border-b border-border">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Users</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Total: {users.length} {showGuests ? 'guest users' : 'users'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const next = !showGuests;
                      setShowGuests(next);
                      fetchUsers(next);
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      showGuests
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:bg-muted'
                    }`}
                  >
                    {showGuests ? 'Showing Guests' : 'Show Guests'}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {loading && !editingUser ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="mt-4 text-muted-foreground">Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No users found.
                </div>
              ) : (
                <div className="space-y-4">
                  {users.map((u) => (
                    <div key={u._id} className="bg-background border border-border rounded-lg p-4">
                      {editingUser && editingUser._id === u._id ? (
                        <form onSubmit={handleUpdateUser} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Email Address
                            </label>
                            <input
                              type="email"
                              value={userFormData.email}
                              onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                              required
                            />
                          </div>

                          <div className="flex items-center gap-6">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={userFormData.isActive}
                                onChange={(e) => setUserFormData({ ...userFormData, isActive: e.target.checked })}
                                className="w-4 h-4 rounded border-border"
                              />
                              <span className="text-sm text-foreground">Active Account</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={userFormData.admin}
                                onChange={(e) => setUserFormData({ ...userFormData, admin: e.target.checked })}
                                className="w-4 h-4 rounded border-border"
                              />
                              <span className="text-sm text-foreground">Admin Privileges</span>
                            </label>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Subscription Platform
                              </label>
                              <Select
                                value={userFormData.subscriptionPlatform}
                                onChange={(e) => setUserFormData({ ...userFormData, subscriptionPlatform: e.target.value })}
                                size="sm"
                                className="w-full"
                              >
                                <option value="">No change</option>
                                <option value="null">None</option>
                                <option value="web">Web</option>
                                <option value="ios">iOS</option>
                                <option value="android">Android</option>
                              </Select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Subscription Renews At
                              </label>
                              <div className="flex gap-2 mb-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const date = new Date();
                                    date.setMonth(date.getMonth() + 1);
                                    setUserFormData({ ...userFormData, subscriptionRenewsAt: date.toISOString().slice(0, 16) });
                                  }}
                                  className="text-xs px-2 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                                >
                                  +1 Month
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const date = new Date();
                                    date.setMonth(date.getMonth() + 6);
                                    setUserFormData({ ...userFormData, subscriptionRenewsAt: date.toISOString().slice(0, 16) });
                                  }}
                                  className="text-xs px-2 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                                >
                                  +6 Months
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const date = new Date();
                                    date.setFullYear(date.getFullYear() + 1);
                                    setUserFormData({ ...userFormData, subscriptionRenewsAt: date.toISOString().slice(0, 16) });
                                  }}
                                  className="text-xs px-2 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                                >
                                  +1 Year
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const date = new Date();
                                    date.setFullYear(date.getFullYear() + 5);
                                    setUserFormData({ ...userFormData, subscriptionRenewsAt: date.toISOString().slice(0, 16) });
                                  }}
                                  className="text-xs px-2 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                                >
                                  +5 Years
                                </button>
                              </div>
                              <input
                                type="datetime-local"
                                value={userFormData.subscriptionRenewsAt}
                                onChange={(e) => setUserFormData({ ...userFormData, subscriptionRenewsAt: e.target.value })}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                              />
                              <button
                                type="button"
                                onClick={() => setUserFormData({ ...userFormData, subscriptionRenewsAt: 'null' })}
                                className="text-xs text-muted-foreground hover:text-foreground mt-1"
                              >
                                Clear renewal date
                              </button>
                            </div>
                          </div>

                          <div className="flex gap-2 pt-2">
                            <button
                              type="submit"
                              disabled={loading}
                              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50"
                            >
                              {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="bg-muted text-foreground px-4 py-2 rounded-lg hover:bg-muted/80"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-foreground break-all">{u.email}</h3>
                              <div className="flex flex-wrap gap-2">
                                {u.admin && (
                                  <span className="text-xs px-2 py-1 rounded bg-warning/20 text-warning font-medium">
                                    Admin
                                  </span>
                                )}
                                {u.isGuest && (
                                  <span className="text-xs px-2 py-1 rounded bg-info/20 text-info font-medium">
                                    Guest
                                  </span>
                                )}
                                <span className={`text-xs px-2 py-1 rounded font-medium ${
                                  u.isActive
                                    ? 'bg-success/20 text-success'
                                    : 'bg-destructive/20 text-destructive'
                                }`}>
                                  {u.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">
                                <span className="font-medium">Joined:</span> {new Date(u.createdAt).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                <span className="font-medium">Flashcards:</span> {u.flashcardCount ?? 0}
                              </p>
                              {u.lastActive && (
                                <p className="text-xs text-muted-foreground">
                                  <span className="font-medium">Last Active:</span> {new Date(u.lastActive).toLocaleDateString()}
                                </p>
                              )}
                              {u.subscriptionRenewsAt && (
                                <p className="text-xs text-muted-foreground">
                                  <span className="font-medium">Premium until:</span> {new Date(u.subscriptionRenewsAt).toLocaleDateString()} ({u.subscriptionPlatform})
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <button
                              onClick={() => startEditingUser(u)}
                              className="text-primary hover:text-primary/80 px-3 py-1 text-sm rounded border border-primary hover:bg-primary/10"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteUser(u._id, u.email)}
                              className="text-destructive hover:text-destructive/80 px-3 py-1 text-sm rounded border border-destructive hover:bg-destructive/10"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {activeTab === 'errors' && (
          <div className="bg-card rounded-lg shadow-lg border border-border">
            <div className="bg-primary/10 px-6 py-4 border-b border-border">
              <div className="flex justify-between items-center flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Error Logs</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Total: {errorLogs.length} (last 30 days)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={errorSourceFilter}
                    onChange={(e) => setErrorSourceFilter(e.target.value)}
                    size="sm"
                  >
                    <option value="">All sources</option>
                    <option value="backend">Backend</option>
                    <option value="mobile">Mobile</option>
                  </Select>
                  <Select
                    value={errorBreakingFilter}
                    onChange={(e) => setErrorBreakingFilter(e.target.value)}
                    size="sm"
                  >
                    <option value="">All severities</option>
                    <option value="true">Breaking only</option>
                    <option value="false">Non-breaking only</option>
                  </Select>
                </div>
              </div>
            </div>

            <div className="p-6">
              {errorLogsLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="mt-4 text-muted-foreground">Loading error logs...</p>
                </div>
              ) : errorLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No error logs found.
                </div>
              ) : (
                <div className="space-y-3">
                  {errorLogs.map((log) => (
                    <div key={log._id} className="bg-background border border-border rounded-lg p-4">
                      <div
                        className="flex justify-between items-start cursor-pointer"
                        onClick={() => setExpandedErrorId(expandedErrorId === log._id ? null : log._id)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className={`text-xs px-2 py-1 rounded font-medium ${
                              log.breaking ? 'bg-destructive/20 text-destructive' : 'bg-muted text-muted-foreground'
                            }`}>
                              {log.breaking ? 'Breaking' : 'Non-breaking'}
                            </span>
                            <span className="text-xs px-2 py-1 rounded bg-info/20 text-info font-medium">
                              {log.source}
                            </span>
                            {log.platform && (
                              <span className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground font-medium">
                                {log.platform}
                              </span>
                            )}
                            <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground font-medium">
                              Occurrences: {log.occurrenceCount ?? 1}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Last occurred: {new Date(log.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-foreground break-words">{log.message}</p>
                        </div>
                      </div>
                      {expandedErrorId === log._id && (
                        <div className="mt-3 pt-3 border-t border-border space-y-2">
                          {log.stack && (
                            <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words bg-muted/30 rounded p-3 overflow-x-auto">
                              {log.stack}
                            </pre>
                          )}
                          {log.context && (
                            <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words bg-muted/30 rounded p-3 overflow-x-auto">
                              {JSON.stringify(log.context, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
